/**
 * Custom error class for API-related errors
 *
 * Every rejected request made through apiClient is an ApiError. Callers
 * switch on `status` first and `code` second (public API contract v1 §4):
 *
 *   if (err.code === ERROR_CODES.SLOT_UNAVAILABLE) { ... }
 *
 * `message` is always safe to show to an end user.
 *
 * @class ApiError
 * @extends Error
 */

/**
 * Error codes from public API contract v1 §4, plus client-side codes for
 * failures that never reached the server.
 */
export const ERROR_CODES = Object.freeze({
  VALIDATION_ERROR: 'validation_error',
  UNAUTHORIZED: 'unauthorized',
  NOT_FOUND: 'not_found',
  METHOD_NOT_ALLOWED: 'method_not_allowed',
  SLOT_UNAVAILABLE: 'slot_unavailable',
  NOTICE_PERIOD: 'notice_period',
  RATE_LIMITED: 'rate_limited',
  INTERNAL_ERROR: 'internal_error',
  CALENDAR_UNAVAILABLE: 'calendar_unavailable',
  SERVICE_UNAVAILABLE: 'service_unavailable',
  // Owner routes: the booking/email is not in a state that allows this action.
  INVALID_STATE: 'invalid_state',
  // Owner content and media routes (contract v1.2 A.12, A.13).
  CONFLICT: 'conflict',
  CONTENT_CONFLICT: 'content_conflict',
  MEDIA_IN_USE: 'media_in_use',
  MEDIA_QUOTA_EXCEEDED: 'media_quota_exceeded',
  MEDIA_TOO_LARGE: 'media_too_large',
  MEDIA_TYPE_NOT_ALLOWED: 'media_type_not_allowed',
  // Client-side only
  NETWORK_ERROR: 'network_error',
  TIMEOUT: 'timeout',
  CANCELED: 'canceled',
  INVALID_RESPONSE: 'invalid_response',
});

class ApiError extends Error {
  /**
   * @param {string} message - Human-readable, user-safe message
   * @param {number} status - HTTP status code (0 when no response was received)
   * @param {string|null} [code] - error.code from the envelope, or a client-side code
   * @param {object} [extra]
   * @param {string|null} [extra.errorId] - error.error_id from a 500 response
   * @param {string|null} [extra.serverMessage] - error.message exactly as the server sent it
   * @param {string|null} [extra.field] - error.field: the dotted path of the
   *   invalid input on owner content/media validation errors, e.g. 'data.schedule.end'
   */
  constructor(message, status, code = null, { errorId = null, serverMessage = null, field = null } = {}) {
    super(message);

    // Maintains proper stack trace for where error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errorId = errorId;
    this.serverMessage = serverMessage;
    this.field = field;
    this.isApiError = true;
    this.isCanceled = code === ERROR_CODES.CANCELED;
  }

  /** @returns {boolean} True for 4xx responses */
  isClientError() {
    return this.status >= 400 && this.status < 500;
  }

  /** @returns {boolean} True for 5xx responses */
  isServerError() {
    return this.status >= 500 && this.status < 600;
  }

  /** @returns {boolean} True when no response was received */
  isNetworkError() {
    return !this.status;
  }

  /**
   * JSON representation for logging / error tracking. Never includes
   * request data, so no client PII ends up in logs.
   * @returns {object}
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      status: this.status,
      code: this.code,
      errorId: this.errorId,
    };
  }

  toString() {
    return `${this.name} [${this.status}${this.code ? ` - ${this.code}` : ''}]: ${this.message}`;
  }
}

export default ApiError;
